"use client";

import { ChevronsDownUpIcon, ChevronsUpDownIcon } from "lucide-react";
import {
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Button } from "../Button";
import { cn } from "../utils";

const DEFAULT_MAX_COLLAPSED_HEIGHT = 300;

export interface CollapsibleMessageBodyProps {
  children: ReactNode;
  isExpanded?: boolean;
  onToggle?: () => void;
  onOverflowChange?: (isOverflowing: boolean) => void;
  maxCollapsedHeight?: number;
  fadeClassName?: string;
}

export const CollapsibleMessageBody = ({
  children,
  isExpanded,
  onToggle,
  onOverflowChange,
  maxCollapsedHeight = DEFAULT_MAX_COLLAPSED_HEIGHT,
  fadeClassName = "to-white",
}: CollapsibleMessageBodyProps) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [internalExpanded, setInternalExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  const isControlled = isExpanded !== undefined;
  const isOpen = isControlled ? isExpanded : internalExpanded;
  const isCollapsed = isOverflowing && !isOpen;

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) {
      return;
    }

    let rafId = 0;

    const checkOverflow = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setContentHeight(contentElement.scrollHeight);
        const nextIsOverflowing =
          contentElement.scrollHeight > maxCollapsedHeight;
        setIsOverflowing((previous) =>
          previous === nextIsOverflowing ? previous : nextIsOverflowing,
        );
      });
    };

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(contentElement);
    checkOverflow();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [maxCollapsedHeight]);

  useEffect(() => {
    onOverflowChange?.(isOverflowing);
  }, [isOverflowing, onOverflowChange]);

  useEffect(() => {
    if (!isOverflowing && !isControlled) {
      setInternalExpanded(false);
    }
  }, [isControlled, isOverflowing]);

  const handleToggleMouseDown = useCallback((event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
  }, []);

  const handleToggle = useCallback(() => {
    if (!isOverflowing) {
      return;
    }

    if (isOpen) {
      requestAnimationFrame(() => {
        viewportRef.current?.scrollTo({ top: 0, behavior: "auto" });
      });
    }

    if (isControlled) {
      onToggle?.();
      return;
    }

    setInternalExpanded((previous) => !previous);
  }, [isControlled, isOpen, isOverflowing, onToggle]);

  const maxHeight = (() => {
    if (isCollapsed) {
      return `${maxCollapsedHeight}px`;
    }
    if (isOverflowing && contentHeight) {
      return `${contentHeight}px`;
    }
    return undefined;
  })();

  return (
    <div className="relative min-w-0">
      <div className="relative min-w-0">
        <div
          ref={viewportRef}
          className={cn(
            "min-w-0 overflow-hidden transition-[max-height] duration-300 ease-in-out",
            isCollapsed && "relative",
          )}
          style={maxHeight ? { maxHeight } : undefined}
        >
          <div ref={contentRef} className="min-w-0">
            {children}
          </div>
          {isCollapsed ? (
            <div
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent",
                fadeClassName,
              )}
            />
          ) : null}
        </div>

        {isOverflowing ? (
          <Button
            type="button"
            variant="pill"
            size="pill"
            onMouseDown={handleToggleMouseDown}
            onClick={handleToggle}
            aria-expanded={isOpen}
            aria-label={isOpen ? "Collapse message" : "Expand message"}
            className="absolute -bottom-[18px] left-1/2 z-[3] -translate-x-1/2 translate-y-1/2"
          >
            {isOpen ? (
              <ChevronsDownUpIcon className="h-3 w-3" />
            ) : (
              <ChevronsUpDownIcon className="h-3 w-3" />
            )}
            <span className="select-none">
              {isOpen ? "Show less" : "Show more"}
            </span>
          </Button>
        ) : null}
      </div>
      {isOverflowing ? <div className="h-3" /> : null}
    </div>
  );
};
